import { z } from 'zod';
import { isValidIsoDay } from './dates';

// ---------------------------------------------------------------------------------------------
// Central de importação Excel/CSV (T-200, RF-45, RN-18, RN-19, RNF-05; DEC-066)

/** Campos que uma coluna da planilha pode alimentar. */
export const IMPORT_FIELDS = ['kpi', 'period', 'unit', 'value', 'target', 'reason'] as const;
export type ImportFieldName = (typeof IMPORT_FIELDS)[number];
export const IMPORT_FIELD_LABELS: Record<ImportFieldName, string> = {
  kpi: 'KPI (código ou nome)',
  period: 'Período',
  unit: 'Unidade (opcional)',
  value: 'Valor realizado',
  target: 'Meta',
  reason: 'Justificativa',
};

/** Mapeamento: para cada campo, o índice da coluna (0 = primeira) ou null quando não vem da planilha. */
export const importMappingSchema = z
  .object({
    kpi: z.number().int().min(0),
    period: z.number().int().min(0),
    unit: z.number().int().min(0).nullable().default(null),
    value: z.number().int().min(0).nullable().default(null),
    target: z.number().int().min(0).nullable().default(null),
    reason: z.number().int().min(0).nullable().default(null),
  })
  .refine((m) => m.value !== null || m.target !== null, {
    message: 'Mapeie o valor realizado, a meta ou os dois.',
    path: ['value'],
  })
  .refine(
    (m) => {
      const used = Object.values(m).filter((v): v is number => v !== null);
      return new Set(used).size === used.length;
    },
    { message: 'Uma coluna alimenta um campo só.', path: ['kpi'] },
  );
export type ImportMapping = z.output<typeof importMappingSchema>;

const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const HINTS: Record<ImportFieldName, string[]> = {
  kpi: ['kpi', 'indicador', 'codigo', 'cod', 'codigo kpi', 'codigo do kpi'],
  period: ['periodo', 'mes', 'competencia', 'data', 'referencia', 'mes ano'],
  unit: ['unidade', 'filial', 'loja', 'unidade de negocio'],
  value: ['valor', 'realizado', 'resultado', 'real'],
  target: ['meta', 'previsto', 'planejado', 'objetivo'],
  reason: ['justificativa', 'motivo', 'observacao', 'obs', 'comentario'],
};

/** Sugestão de mapeamento pelos títulos das colunas (sem acento e sem caixa). */
export function suggestMapping(headers: string[]): Partial<Record<ImportFieldName, number>> {
  const out: Partial<Record<ImportFieldName, number>> = {};
  const taken = new Set<number>();
  for (const field of IMPORT_FIELDS) {
    const index = headers.findIndex((h, i) => !taken.has(i) && HINTS[field].includes(norm(h)));
    if (index >= 0) {
      out[field] = index;
      taken.add(index);
    }
  }
  return out;
}

/**
 * CSV de planilha brasileira: separador ";" ou "," (detectado pela primeira linha), aspas com "" dentro,
 * quebras de linha dentro de aspas, BOM do Excel e linhas em branco ignoradas.
 */
export function parseCsv(text: string): string[][] {
  // Marca de ordem de bytes (BOM) que o Excel põe no início do CSV.
  const src = text.startsWith(String.fromCharCode(0xfeff)) ? text.slice(1) : text;
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const sep = (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.map((r) => r.map((c) => c.trim())).filter((r) => r.some((c) => c !== ''));
}

/** Número como a planilha brasileira escreve ("1.234,56", "1234,56", "R$ 2.500", "12%", "-3,5") ou já número. */
export function parseNumberBr(raw: string | number): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let t = raw.replace(/R\$|\s|%/g, '');
  if (t === '') return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const MONTH_NAMES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const lastDay = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Período da linha: um dia ("31/03/2026", "2026-03-31") ou um mês ("03/2026", "2026-03", "mar/2026",
 * "março de 2026"); o mês vira o último dia dele. Datas do Excel chegam como Date. null = não reconhecido.
 */
export function parsePeriod(
  raw: string | Date,
): { year: number; month: number; day: string; dayGiven: boolean } | null {
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    const y = raw.getUTCFullYear();
    const m = raw.getUTCMonth() + 1;
    return { year: y, month: m, day: `${y}-${pad(m)}-${pad(raw.getUTCDate())}`, dayGiven: true };
  }
  const t = norm(raw).replace(/ de /g, ' ');
  const s = raw.trim();
  let y: number | null = null;
  let m: number | null = null;
  let d: number | null = null;
  let match: RegExpMatchArray | null;
  if ((match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) [d, m, y] = [+match[1]!, +match[2]!, +match[3]!];
  else if ((match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/))) [y, m, d] = [+match[1]!, +match[2]!, +match[3]!];
  else if ((match = s.match(/^(\d{1,2})\/(\d{4})$/))) [m, y] = [+match[1]!, +match[2]!];
  else if ((match = s.match(/^(\d{4})-(\d{1,2})$/))) [y, m] = [+match[1]!, +match[2]!];
  else if ((match = t.match(/^([a-z]{3})[a-z]* (\d{4})$/))) {
    const index = MONTH_NAMES.indexOf(match[1]!);
    if (index >= 0) [m, y] = [index + 1, +match[2]!];
  }
  if (y === null || m === null || m < 1 || m > 12 || y < 2000 || y > 2100) return null;
  const day = d === null ? lastDay(y, m) : d;
  const iso = `${y}-${pad(m)}-${pad(day)}`;
  if (!isValidIsoDay(iso)) return null;
  return { year: y, month: m, day: iso, dayGiven: d !== null };
}

// --- Contrato da API ---------------------------------------------------------------------------

export const IMPORT_STATUSES = ['DRAFT', 'VALIDATED', 'QUEUED', 'PROCESSING', 'DONE', 'FAILED'] as const;
export type ImportStatusName = (typeof IMPORT_STATUSES)[number];
export const IMPORT_STATUS_LABELS: Record<ImportStatusName, string> = {
  DRAFT: 'Mapeando colunas',
  VALIDATED: 'Validada',
  QUEUED: 'Na fila',
  PROCESSING: 'Processando',
  DONE: 'Concluída',
  FAILED: 'Falhou',
};

export const importIssueSchema = z.object({
  /** Linha da planilha (1 = títulos). */
  line: z.number().int(),
  field: z.enum(IMPORT_FIELDS).nullable(),
  message: z.string(),
});
export type ImportIssue = z.infer<typeof importIssueSchema>;

export const importSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  status: z.enum(IMPORT_STATUSES),
  headers: z.array(z.string()),
  /** Primeiras linhas (até 10), para conferir o mapeamento. */
  sample: z.array(z.array(z.string())),
  rowCount: z.number().int(),
  mapping: z.record(z.string(), z.number().nullable()).nullable(),
  suggested: z.record(z.string(), z.number()),
  validRows: z.number().int().nullable(),
  issues: z.array(importIssueSchema),
  accepted: z.number().int().nullable(),
  rejected: z.number().int().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  createdById: z.string().uuid().nullable(),
  createdByName: z.string(),
  finishedAt: z.string().nullable(),
  /** Processamento sem andamento há mais de 35 minutos: quem enviou pode confirmar de novo para retomar. */
  stalled: z.boolean(),
});
export type Import = z.infer<typeof importSchema>;

export const importListSchema = z.object({
  items: z.array(importSchema.omit({ sample: true, issues: true, headers: true, suggested: true })),
});

export const importValidateInputSchema = z.object({ mapping: importMappingSchema });
export const importConfirmInputSchema = z.object({
  /** Justificativa padrão para linhas que mudam valor já lançado ou de mês fechado (sem coluna própria). */
  reason: z.string().trim().max(1000).optional(),
});

export const importTemplateInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do modelo.').max(80),
  /** Mapeamento pelos títulos das colunas (o modelo serve para planilhas com a mesma estrutura). */
  columns: z
    .object({
      kpi: z.string().min(1),
      period: z.string().min(1),
      unit: z.string().min(1).nullable().default(null),
      value: z.string().min(1).nullable().default(null),
      target: z.string().min(1).nullable().default(null),
      reason: z.string().min(1).nullable().default(null),
    })
    .refine((c) => c.value !== null || c.target !== null, {
      message: 'Mapeie o valor realizado, a meta ou os dois.',
      path: ['value'],
    }),
});
export const importTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  columns: importTemplateInputSchema.shape.columns,
  createdAt: z.string(),
  createdByName: z.string(),
});
export type ImportTemplate = z.infer<typeof importTemplateSchema>;

/**
 * Aplica um modelo (títulos) aos títulos da planilha: o mapeamento (null quando falta coluna obrigatória ou não
 * sobra valor nem meta) e os títulos do modelo que a planilha não tem, para a tela avisar.
 */
export function mappingFromTemplate(
  columns: ImportTemplate['columns'],
  headers: string[],
): { mapping: ImportMapping | null; missing: string[] } {
  const missing = Object.values(columns).filter(
    (title): title is string => title !== null && !headers.some((h) => norm(h) === norm(title)),
  );
  const at = (title: string | null) =>
    title === null ? null : headers.findIndex((h) => norm(h) === norm(title));
  const kpi = at(columns.kpi);
  const period = at(columns.period);
  const optional = (title: string | null) => {
    const i = at(title);
    return i === null || i < 0 ? null : i;
  };
  if (kpi === null || kpi < 0 || period === null || period < 0) return { mapping: null, missing };
  const parsed = importMappingSchema.safeParse({
    kpi,
    period,
    unit: optional(columns.unit),
    value: optional(columns.value),
    target: optional(columns.target),
    reason: optional(columns.reason),
  });
  return { mapping: parsed.success ? parsed.data : null, missing };
}

// --- Log da importação --------------------------------------------------------------------------

export const IMPORT_LINE_STATUS_LABELS = { ACCEPTED: 'Aceita', REJECTED: 'Recusada' } as const;

/** Valores da linha como vieram da planilha (guardados no log). */
export type ImportLineData = Partial<Record<ImportFieldName, string | null>>;

const csvCell = (v: string | number | null | undefined) => {
  const t = v === null || v === undefined ? '' : String(v);
  return /[";\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

/**
 * O log como CSV para abrir no Excel em português: separador ";", BOM do UTF-8 (para os acentos) e uma linha por
 * linha da planilha, com a situação, o motivo e os valores.
 */
export function importLogCsv(
  lines: {
    line: number;
    status: keyof typeof IMPORT_LINE_STATUS_LABELS;
    message: string | null;
    data: unknown;
  }[],
): string {
  const header = [
    'Linha',
    'Situação',
    'Motivo',
    'KPI',
    'Período',
    'Unidade',
    'Valor',
    'Meta',
    'Justificativa',
  ];
  const rows = lines.map((l) => {
    const d = (l.data ?? {}) as ImportLineData;
    return [
      l.line,
      IMPORT_LINE_STATUS_LABELS[l.status],
      l.message,
      d.kpi,
      d.period,
      d.unit,
      d.value,
      d.target,
      d.reason,
    ]
      .map(csvCell)
      .join(';');
  });
  return String.fromCharCode(0xfeff) + [header.join(';'), ...rows].join('\r\n') + '\r\n';
}
